import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography, createFilterOptions,
} from '@mui/material';
import { toast } from 'react-toastify';
import { loanApi } from '../../../api/loanApi';
import {
  CreateLoanRequestPayload,
  LoanEmployeeOptionDto,
  LoanInterestMethod,
  LoanInterestMethodLabels,
  LoanSchedulePreviewDto,
  LoanType,
  LoanTypeLabels,
} from '../../../types/loanTypes';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const money = (value: number) =>
  `₹${(value ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Sanity ceiling on annual interest rate - guards against fat-finger entries (e.g. "50000"
// instead of "5.0"). Kept in sync with LoanWorkflowHelper.MaxInterestRatePercent on the backend.
const MAX_INTEREST_RATE_PERCENT = 100;

const employeeLabel = (employee: LoanEmployeeOptionDto) =>
  `${employee.name} (${employee.employeeCode})${employee.departmentName ? ` — ${employee.departmentName}` : ''}`;

// Match on the fields the placeholder promises - name, employee code, department - rather than
// on the rendered label, so the em-dash and brackets in the label cannot swallow a query.
const filterEmployees = createFilterOptions<LoanEmployeeOptionDto>({
  trim: true,
  stringify: (employee) =>
    `${employee.name} ${employee.employeeCode} ${employee.departmentName ?? ''}`,
});

interface Props {
  open: boolean;
  mode: 'hr' | 'employee';
  onClose: () => void;
  onSaved: () => void;
}

const now = new Date();

const LoanFormDialog: React.FC<Props> = ({ open, mode, onClose, onSaved }) => {
  const [employees, setEmployees] = useState<LoanEmployeeOptionDto[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [loanType, setLoanType] = useState<LoanType>(LoanType.Loan);
  const [principal, setPrincipal] = useState('');
  const [interestMethod, setInterestMethod] = useState<LoanInterestMethod>(LoanInterestMethod.None);
  const [interestRate, setInterestRate] = useState('');
  const [installments, setInstallments] = useState('12');
  const [startMonth, setStartMonth] = useState<number>(now.getMonth() + 1);
  const [startYear, setStartYear] = useState<number>(now.getFullYear());
  const [purpose, setPurpose] = useState('');
  const [autoApprove, setAutoApprove] = useState(mode === 'hr');
  const [preview, setPreview] = useState<LoanSchedulePreviewDto | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmployeeId('');
    setLoanType(LoanType.Loan);
    setPrincipal('');
    setInterestMethod(LoanInterestMethod.None);
    setInterestRate('');
    setInstallments('12');
    setStartMonth(now.getMonth() + 1);
    setStartYear(now.getFullYear());
    setPurpose('');
    setAutoApprove(mode === 'hr');
    setPreview(null);
    if (mode === 'hr') {
      loanApi.getEmployeeOptions().then(setEmployees).catch(() => toast.error('Failed to load employees'));
    }
  }, [open, mode]);

  const principalNum = Number(principal) || 0;
  const installmentsNum = Number(installments) || 0;
  const rateNum = interestMethod === LoanInterestMethod.None ? 0 : Number(interestRate) || 0;
  const rateExceedsMax = interestMethod !== LoanInterestMethod.None && rateNum > MAX_INTEREST_RATE_PERCENT;

  // A recovery start before the current month has ambiguous meaning (overdue? backdated?), so
  // the current month is the earliest selectable one for the current year.
  const minSelectableMonth = startYear === now.getFullYear() ? now.getMonth() + 1 : 1;
  const availableMonths = MONTHS.map((m, i) => ({ month: m, value: i + 1 })).filter((m) => m.value >= minSelectableMonth);

  useEffect(() => {
    if (startMonth < minSelectableMonth) setStartMonth(minSelectableMonth);
  }, [startYear, minSelectableMonth, startMonth]);

  const canPreview = principalNum > 0 && installmentsNum > 0;

  useEffect(() => {
    if (!open || !canPreview) {
      setPreview(null);
      return;
    }
    const handle = setTimeout(() => {
      loanApi
        .previewSchedule({
          principalAmount: principalNum,
          interestMethod,
          interestRatePercent: rateNum,
          totalInstallments: installmentsNum,
          repaymentStartMonth: startMonth,
          repaymentStartYear: startYear,
        })
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(handle);
  }, [open, canPreview, principalNum, interestMethod, rateNum, installmentsNum, startMonth, startYear]);

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === employeeId),
    [employees, employeeId],
  );

  const handleSave = async () => {
    if (mode === 'hr' && !employeeId) {
      toast.error('Select an employee');
      return;
    }
    if (principalNum <= 0 || installmentsNum <= 0) {
      toast.error('Enter a valid amount and number of installments');
      return;
    }
    if (rateExceedsMax) {
      toast.error(`Annual Interest Rate cannot exceed ${MAX_INTEREST_RATE_PERCENT}%`);
      return;
    }
    if (startYear < now.getFullYear() || (startYear === now.getFullYear() && startMonth < now.getMonth() + 1)) {
      toast.error('Recovery start month/year cannot be in the past');
      return;
    }

    const payload: CreateLoanRequestPayload = {
      // The server ignores this for the self-service endpoint (it resolves the current user's
      // own employee id), and an empty string here fails Guid model binding with a bare 400.
      ...(mode === 'hr' ? { employeeId } : {}),
      loanType,
      principalAmount: principalNum,
      interestMethod,
      interestRatePercent: rateNum,
      totalInstallments: installmentsNum,
      repaymentStartMonth: startMonth,
      repaymentStartYear: startYear,
      purpose: purpose || undefined,
      autoApprove: mode === 'hr' ? autoApprove : false,
    };

    setSaving(true);
    try {
      if (mode === 'hr') {
        await loanApi.createLoan(payload);
        toast.success(autoApprove ? 'Loan created and approved' : 'Loan request created');
      } else {
        await loanApi.requestLoan(payload);
        toast.success('Loan / advance request submitted');
      }
      onSaved();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.response?.data || 'Failed to save loan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{mode === 'hr' ? 'New Loan / Advance' : 'Request Loan / Advance'}</DialogTitle>
      <DialogContent>
        {/* DialogContent drops its top padding when a DialogTitle precedes it and clips overflow,
            so the first row needs enough headroom for a shrunk outlined label, which floats ~9px
            above the input. Anything less and the Employee / Type labels get cut off on focus. */}
        <Grid container spacing={2} sx={{ mt: 1.5 }}>
          {mode === 'hr' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                fullWidth
                options={employees}
                value={selectedEmployee ?? null}
                onChange={(_, option) => setEmployeeId(option?.id ?? '')}
                filterOptions={filterEmployees}
                getOptionLabel={employeeLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionDisabled={(option) => option.hasOpenLoan}
                noOptionsText="No employees found"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    placeholder="Search by name, code or department"
                    helperText={selectedEmployee?.hasOpenLoan ? 'This employee already has an open loan' : ' '}
                    error={Boolean(selectedEmployee?.hasOpenLoan)}
                  />
                )}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select fullWidth label="Type" value={loanType} onChange={(e) => setLoanType(Number(e.target.value) as LoanType)}>
              {Object.entries(LoanTypeLabels).map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth type="number" label="Principal Amount" value={principal}
              onChange={(e) => setPrincipal(e.target.value)} InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth type="number" label="Number of Installments" value={installments}
              onChange={(e) => setInstallments(e.target.value)} InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select fullWidth label="Interest Method" value={interestMethod}
              onChange={(e) => setInterestMethod(Number(e.target.value) as LoanInterestMethod)}
            >
              {Object.entries(LoanInterestMethodLabels).map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth type="number" label="Annual Interest Rate (%)" value={interestRate}
              disabled={interestMethod === LoanInterestMethod.None}
              onChange={(e) => setInterestRate(e.target.value)}
              InputProps={{ inputProps: { min: 0, max: MAX_INTEREST_RATE_PERCENT, step: 0.25 } }}
              error={rateExceedsMax}
              helperText={rateExceedsMax ? `Cannot exceed ${MAX_INTEREST_RATE_PERCENT}%` : ' '}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select fullWidth label="Recovery Starts — Month" value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))}>
              {availableMonths.map((m) => <MenuItem key={m.month} value={m.value}>{m.month}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select fullWidth label="Recovery Starts — Year" value={startYear} onChange={(e) => setStartYear(Number(e.target.value))}>
              {[0, 1, 2].map((offset) => {
                const y = now.getFullYear() + offset;
                return <MenuItem key={y} value={y}>{y}</MenuItem>;
              })}
            </TextField>
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline minRows={2} label="Purpose / Remarks" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
          </Grid>
          {mode === 'hr' && (
            <Grid size={12}>
              <TextField
                select fullWidth label="Approval" value={autoApprove ? 'auto' : 'workflow'}
                onChange={(e) => setAutoApprove(e.target.value === 'auto')}
                helperText="Auto-approve for an already-sanctioned loan, or route through the approval workflow."
              >
                <MenuItem value="auto">Auto-approve (sanctioned)</MenuItem>
                <MenuItem value="workflow">Send for approval</MenuItem>
              </TextField>
            </Grid>
          )}
        </Grid>

        {preview && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={3} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="body2"><strong>Installment:</strong> {money(preview.installmentAmount)}</Typography>
              <Typography variant="body2"><strong>Total Interest:</strong> {money(preview.totalInterest)}</Typography>
              <Typography variant="body2"><strong>Total Payable:</strong> {money(preview.totalPayable)}</Typography>
            </Stack>
            <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell align="right">Principal</TableCell>
                    <TableCell align="right">Interest</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.installments.map((i) => (
                    <TableRow key={i.installmentNumber}>
                      <TableCell>{i.installmentNumber}</TableCell>
                      <TableCell>{MONTHS[i.dueMonth - 1]?.slice(0, 3)} {i.dueYear}</TableCell>
                      <TableCell align="right">{money(i.principalComponent)}</TableCell>
                      <TableCell align="right">{money(i.interestComponent)}</TableCell>
                      <TableCell align="right">{money(i.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || rateExceedsMax} sx={{ textTransform: 'none' }}>
          {saving ? 'Saving…' : mode === 'hr' ? 'Create' : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoanFormDialog;
